import { isPrivateResource } from '../../services/questionnaireService';
import {
    QuestionnaireLifecycle,
    QuestionnaireSummary,
    PractitionerQuestionnaireBundle,
    QuestionnaireResource,
    TaskSummary,
    TaskBundle,
    TaskResource,
} from './types';

export type AssignmentWindow = {
    startsOn: Date;
    endsOn?: Date;
};

export const createAssignmentWindows = (
    startsOn: Date,
    endsOn?: Date,
    recurrenceType?: 'daily' | 'weekly',
    recurrenceEndDate?: Date,
): AssignmentWindow[] => {
    const availabilityDuration = endsOn ? endsOn.getTime() - startsOn.getTime() : undefined;
    if (availabilityDuration !== undefined && availabilityDuration <= 0) {
        throw new RangeError('End date must be after start date.');
    }

    const occurrenceStarts: Date[] = [];
    if (recurrenceType && recurrenceEndDate) {
        const currentDate = new Date(startsOn);
        const finalStartDate = new Date(recurrenceEndDate);
        finalStartDate.setHours(23, 59, 59, 999);

        while (currentDate <= finalStartDate) {
            occurrenceStarts.push(new Date(currentDate));
            currentDate.setDate(currentDate.getDate() + (recurrenceType === 'daily' ? 1 : 7));
        }
    } else {
        occurrenceStarts.push(new Date(startsOn));
    }

    return occurrenceStarts.map((occurrenceStart) => ({
        startsOn: occurrenceStart,
        endsOn:
            availabilityDuration === undefined ? undefined : new Date(occurrenceStart.getTime() + availabilityDuration),
    }));
};

export const isExpiredUnanswered = (
    task: Pick<TaskSummary, 'patientId' | 'endsOn' | 'hasResponse'>,
    now: number = Date.now(),
): boolean => {
    if (!task.patientId || !task.endsOn || task.hasResponse) {
        return false;
    }

    const endTime = new Date(task.endsOn).getTime();
    return Number.isFinite(endTime) && endTime <= now;
};

// The backend stores the creator as `Practitioner/<id>`.
const isPublishedBy = (publisher: string | undefined, practitionerId: string | undefined): boolean =>
    Boolean(publisher && practitionerId && publisher.split('/').pop() === practitionerId);

export const toQuestionnaireSummaries = (
    bundle: PractitionerQuestionnaireBundle | undefined,
    fallbackTitle: string,
    currentPractitionerId?: string,
): QuestionnaireSummary[] => {
    if (!bundle?.entry?.length) {
        return [];
    }

    return bundle.entry
        .map((entry) => entry.resource)
        .filter((resource): resource is QuestionnaireResource => Boolean(resource?.id))
        .map((resource) => {
            const displayName = resource?.name?.trim() || resource?.title?.trim() || resource?.id || fallbackTitle;
            return {
                id: resource.id as string,
                displayName,
                status: resource.status,
                updatedAt: resource.meta?.lastUpdated,
                title: resource.title,
                name: resource.name,
                isPrivate: isPrivateResource(resource),
                isMine: isPublishedBy(resource.publisher, currentPractitionerId),
                versionId: resource.meta?.versionId,
            };
        });
};

const QUESTIONNAIRE_LIFECYCLE_EXTENSION_URL = 'http://quala.health/fhir/StructureDefinition/questionnaire-lifecycle';

const toQuestionnaireLifecycle = (resource: TaskResource): QuestionnaireLifecycle | undefined => {
    const value = resource.extension?.find(
        (extension) => extension.url === QUESTIONNAIRE_LIFECYCLE_EXTENSION_URL,
    )?.valueCode;
    return value === 'archived' || value === 'replaced' ? value : undefined;
};

export const toTaskSummaries = (
    bundle: TaskBundle | undefined,
    taskResourcesMap?: Map<string, TaskResource>,
): TaskSummary[] => {
    if (!bundle?.entry?.length) {
        return [];
    }

    return bundle.entry
        .map((entry) => entry.resource)
        .filter((resource): resource is TaskResource => Boolean(resource?.id))
        .map((resource) => {
            const patientRef = resource.owner?.reference || '';
            const questionnaireRef = resource.focus?.reference || '';
            const patientId = patientRef.includes('/') ? patientRef.split('/').pop() : patientRef;
            const questionnaireId = questionnaireRef.includes('/')
                ? questionnaireRef.split('/').pop()
                : questionnaireRef;

            // Store the full resource if map is provided
            if (taskResourcesMap && resource.id) {
                taskResourcesMap.set(resource.id, resource);
            }

            return {
                id: resource.id as string,
                patientId,
                patientName: resource.owner?.display || patientId || '',
                questionnaireId,
                questionnaireName: resource.focus?.display || questionnaireId || '',
                status: resource.status,
                updatedAt: resource.meta?.lastUpdated,
                startsOn: resource.executionPeriod?.start,
                endsOn: resource.executionPeriod?.end,
                hasResponse: Boolean(resource.output?.length),
                questionnaireLifecycle: toQuestionnaireLifecycle(resource),
            };
        });
};
